import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

// Client-side amortization (French system) — supports any term & monthly rate.
export const amortize = (principal, termMonths, monthlyRate) => {
  const p = Number(principal) || 0;
  const n = Math.max(1, Math.round(termMonths));
  const r = Number(monthlyRate) || 0;
  const payment = r === 0 ? p / n : (p * (r * Math.pow(1 + r, n))) / (Math.pow(1 + r, n) - 1);
  const schedule = [];
  let balance = p;
  for (let month = 1; month <= n; month++) {
    const interest = balance * r;
    const capital = payment - interest;
    balance = Math.max(0, balance - capital);
    schedule.push({
      month,
      payment: Math.round(payment * 100) / 100,
      interest: Math.round(interest * 100) / 100,
      principal: Math.round(capital * 100) / 100,
      balance: Math.round(balance * 100) / 100,
    });
  }
  const total = Math.round(payment * n * 100) / 100;
  return { schedule, payment: Math.round(payment * 100) / 100, total, interest: Math.round((total - p) * 100) / 100 };
};

const money = (v, currency, locale) =>
  new Intl.NumberFormat(locale || 'es-MX', { style: 'currency', currency: currency || 'MXN', maximumFractionDigits: 2 }).format(v || 0);

// Generates and downloads a branded amortization PDF.
// opts: { principal, currency, locale, termMonths, monthlyRate, priceUsd, schedule?, payment?, total?, interest?, parties? , title?, subtitle? }
export const downloadAmortizationPdf = (opts) => {
  const {
    principal, currency = 'MXN', locale = 'es-MX', termMonths, monthlyRate,
    priceUsd = 1800, parties = null, title = 'Tabla de Amortización', subtitle = 'Simulación de financiamiento',
    contractId = null, signatureName = null, signedAt = null,
  } = opts;

  let { schedule, payment, total, interest } = opts;
  if (!schedule) {
    const a = amortize(principal, termMonths, monthlyRate);
    schedule = a.schedule; payment = a.payment; total = a.total; interest = a.interest;
  }

  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const W = doc.internal.pageSize.getWidth();
  const navy = [10, 36, 99];
  const magenta = [233, 30, 140];

  // Header band
  doc.setFillColor(...navy);
  doc.rect(0, 0, W, 70, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.text('Ed.Fi', 40, 34);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(233, 30, 140);
  doc.text('x  EduPro Global Education', 90, 34);
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(9);
  doc.text('Estudia, trabaja y vive en España', 40, 52);

  doc.setTextColor(...navy);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text(title, 40, 100);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(90, 90, 90);
  doc.text(subtitle, 40, 116);

  // Summary box
  let y = 138;
  const rows = [
    ['Programa', `EduPro Global Education (${priceUsd.toLocaleString('en-US')} USD)`],
    ['Monto financiado', money(principal, currency, locale)],
    ['Plazo', `${termMonths} meses`],
    ['Tasa de interés', `${(monthlyRate * 100).toFixed(2)}% mensual`],
    ['Pago mensual', money(payment, currency, locale)],
    ['Total a pagar', money(total, currency, locale)],
    ['Intereses totales', money(interest, currency, locale)],
    ['Pago anticipado', 'Sin penalización'],
  ];
  if (parties?.deudor) rows.unshift(['Deudor', parties.deudor]);
  if (parties?.aval) rows.splice(1, 0, ['Aval / Co-deudor', parties.aval]);

  autoTable(doc, {
    startY: y,
    theme: 'plain',
    body: rows,
    styles: { fontSize: 9, cellPadding: 3 },
    columnStyles: { 0: { fontStyle: 'bold', textColor: navy, cellWidth: 150 }, 1: { textColor: [40, 40, 40] } },
  });

  y = doc.lastAutoTable.finalY + 18;
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...navy);
  doc.setFontSize(12);
  doc.text('Calendario de pagos', 40, y);

  autoTable(doc, {
    startY: y + 8,
    head: [['#', 'Pago', 'Capital', 'Interés', 'Saldo']],
    body: schedule.map((s) => [
      s.month,
      money(s.payment, currency, locale),
      money(s.principal, currency, locale),
      money(s.interest, currency, locale),
      money(s.balance, currency, locale),
    ]),
    styles: { fontSize: 8, cellPadding: 3 },
    headStyles: { fillColor: navy, textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [244, 246, 251] },
    columnStyles: { 0: { halign: 'center', cellWidth: 34 }, 1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' } },
    margin: { left: 40, right: 40 },
  });

  if (signatureName) {
    const fy = doc.lastAutoTable.finalY + 30;
    doc.setDrawColor(...magenta);
    doc.line(40, fy, 240, fy);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(...navy);
    doc.setFontSize(14);
    doc.text(signatureName, 45, fy - 6);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(120, 120, 120);
    doc.text(`Firmado digitalmente${signedAt ? ' · ' + new Date(signedAt).toLocaleString('es-MX') : ''}`, 40, fy + 14);
    if (contractId) doc.text(`Folio: ${contractId}`, 40, fy + 26);
  }

  // Footer disclaimer on each page
  const pages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    const H = doc.internal.pageSize.getHeight();
    doc.setFontSize(7);
    doc.setTextColor(150, 150, 150);
    doc.text('Documento informativo generado por Ed.Fi. Los montos pueden variar según el perfil crediticio y el tipo de cambio vigente.', 40, H - 24);
    doc.text(`Página ${i} de ${pages}`, W - 90, H - 24);
  }

  const fname = signatureName ? `contrato-edupro-${contractId || 'edfi'}.pdf` : `simulacion-edupro-${currency.toLowerCase()}-${termMonths}m.pdf`;
  doc.save(fname);
};
